import { Box, Typography } from '@mui/material'
import React, { FC, useContext, useMemo } from 'react'
import { EmsProductTypeValue } from 'src/Routes/Installation/InstallationModel'
import { makeStyles } from 'tss-react/mui'
import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import SystemCard from './SystemCard'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { MasterDeviceType } from '../types'
import { useFarmContext } from 'src/Elements/Farm'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { Installation } from 'src/Libs/installations'

interface SystemsProps {
  installation: Installation | undefined
}

interface StorageElement {
  id: string | number
  type?: EmsProductTypeValue
  isDeleted?: boolean
  isOffline?: boolean
  masterDeviceType?: MasterDeviceType
}

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    width: '100%',
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 140px)',
    gridAutoRows: '1fr',
    marginLeft: '-12px',

    '@media(max-width: 600px)': {
      justifyContent: 'center',
    },
  },
  emptySystemsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.spacing(2)} 0`,
    width: '100%',
    gridColumn: '1 / end',
    boxSizing: 'border-box',
  },
}))

/**
 * This component represents container with cards for systems that related to selected installation
 * @returns Systems Component
 **/
const Systems: FC<SystemsProps> = ({ installation }) => {
  const { isFarm, farmMaster, farmClients } = useFarmContext()
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { deviceLinks: allDeviceLinks, installationLoading: isLoading } = useActiveInstallationContext()
  const { isServerOnline, offlineClientStorageIds, isStorageOnline } = useContext(InstallationOnlineStatusContext)

  const storagesElements: StorageElement[] = useMemo(() => {
    const storageDeviceLinks = allDeviceLinks.filter(device => device.deviceType === 'storage' || device.deviceType === 'emc')
    const deletedStatusByDeviceId = Object.fromEntries(storageDeviceLinks.map(elem => [elem.deviceId, !!elem.decommissionedAt]))

    if (!isFarm) {
      /* First storage from the list can be used in case when installation is not a farm(farm is installation with only one device) */
      const firstStorage = storageDeviceLinks[0]

      if (!firstStorage?.deviceId) return []

      return [
        {
          id: firstStorage?.deviceId,
          isDeleted: deletedStatusByDeviceId[firstStorage?.deviceId],
          type: firstStorage?.productName,
          isOffline: !isStorageOnline,
        },
      ]
    }

    const virtualMasterDeviceId = farmMaster?.storageId ?? ''
    /* Physical device id has the same device id as virtual, but 6th symbol is different(0 instead of 1) in SN */
    const physicalMasterDeviceId = virtualMasterDeviceId.slice(0, 6) + '0' + virtualMasterDeviceId.slice(7)

    const storages: StorageElement[] = [
      {
        id: virtualMasterDeviceId,
        isDeleted: deletedStatusByDeviceId[virtualMasterDeviceId],
        type: farmMaster?.productType,
        masterDeviceType: MasterDeviceType.VIRTUAL,
        isOffline: !isServerOnline,
      },
      ...(farmClients?.map(({ productType, storageId }) => ({
        id: storageId,
        isDeleted: deletedStatusByDeviceId[storageId],
        type: productType,
        masterDeviceType: physicalMasterDeviceId === `${storageId}` ? MasterDeviceType.PHYSICAL : undefined,
        isOffline: offlineClientStorageIds?.includes(storageId.toString()),
      })) ?? []),
    ]

    return storages
  }, [farmMaster, farmClients, isFarm, allDeviceLinks, isServerOnline, offlineClientStorageIds, isStorageOnline])

  if (isLoading) {
    return (
      <Box>
        <EditorSkeleton />
      </Box>
    )
  }

  return (
    <Box className={classes.mainContainer}>
      {storagesElements?.length ? (
        storagesElements.map((elem, index) => (
          <SystemCard
            id={`${elem.id}`}
            type={elem.type}
            installation={installation}
            key={`${elem.id}-${index}`}
            isDeleted={elem.isDeleted}
            masterDeviceType={elem.masterDeviceType}
            isOffline={elem.isOffline}
          />
        ))
      ) : (
        <Box className={classes.emptySystemsContainer}>
          <Typography variant="body2">{t('installation.details.systems.no-systems')}</Typography>
        </Box>
      )}
    </Box>
  )
}

export default Systems
